/** @format */

import React from "react";
import { Tag, Trash2, Info, Lock } from "lucide-react";
import { LoadingSpinner } from "./LoadingSpinner"; // Assuming path is correct

export const CartSummary = ({
	totals, // { items: [], msrp, ourPrice, savings, displayMsrp, displayOurPrice, displaySavings, country, subTotal, tax, shipping }
	promoCode,
	promoAmount = 0, // Default to 0
	onPromoChange = null,
	onPromoApply = null,
	onPromoRemove = null, // Added remove handler prop
	promoError = "",
	isApplyingPromo = false, // Loading state for promo
	onCheckout = null,
	isCheckingOut = false, // Loading state for checkout
}) => {
	const finalTotal = totals?.subTotal || 0; // Final total after promo, shipping, tax from calculateTotals
	return (
		<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6 space-y-5">
			<h3 className="font-semibold text-lg pb-2 border-b border-gray-100">
				Order Summary
			</h3>

			{/* Price Breakdown */}
			<div className="space-y-2 text-sm">
				<div className="flex justify-between">
					<span className="text-gray-600">Retail</span>
					<span className="font-medium">{totals?.displayMsrp || "$0.00"}</span>
				</div>

				{/* Savings Display (MSRP vs Our Price) */}
				{totals?.savings > 0 && (
					<div className="flex justify-between text-primary">
						<span>40% Discount</span>
						<span>-{totals?.displaySavings || "$0.00"}</span>
					</div>
				)}
				{totals?.operationDiscount > 0 && (
					<div className="flex justify-between text-blue-600">
						<span>Operation Upgrade</span>
						<span>-{totals?.displayOperationSavings || "$0.00"}</span>
					</div>
				)}
				{/* Subtotal (based on 'Our Price' of items) */}
				<div className="flex justify-between">
					<span className="text-gray-600">Subtotal</span>
					<span className="font-medium">
						{totals?.displayOurPrice || "$0.00"}
					</span>
				</div>

				{/* Promo Discount Display */}
				{promoAmount > 0 && (
					<div className="flex justify-between text-blue-600">
						<span>Promo Code ({promoCode})</span>
						<span>
							-
							{totals?.country === "USA"
								? `$${promoAmount.toFixed(2)} USD`
								: `$${promoAmount.toFixed(2)} CAD`}
						</span>
					</div>
				)}

				{/* Shipping */}
				<div className="flex justify-between">
					<span className="text-gray-600">Shipping</span>
					<span className="font-medium">
						{/* Display FREE or calculated value clearly */}
						{totals?.shipping === 0 ? (
							<span className="text-green-600">FREE</span>
						) : totals?.shipping ? (
							`$${totals.shipping.toFixed(2)} ${
								totals.country === "USA" ? "USD" : "CAD"
							}`
						) : (
							"Calculated at next step"
						)}
					</span>
				</div>

				{/* Tax */}
				<div className="flex justify-between">
					<span className="text-gray-600">
						Estimated Tax
						{/* Optional: Tooltip explaining tax calculation */}
						<button
							className="ml-1 text-gray-400 hover:text-gray-600"
							title="Taxes are estimated based on your potential shipping address and calculated during checkout."
						>
							<Info size={12} />
						</button>
					</span>
					<span className="font-medium">
						{totals?.tax === 0
							? "Calculated at checkout"
							: `$${totals.tax} ${totals.country === "USA" ? "USD" : "CAD"}`}
					</span>
				</div>

				{/* Final Total */}
				<div className="pt-3 border-t border-gray-200">
					<div className="flex justify-between font-semibold text-lg">
						<span>Estimated Total</span>
						{/* Use final total calculated by utility */}
						<span>
							{`$${finalTotal.toFixed(2)} ${
								totals?.country === "USA" ? `USD` : `CAD`
							}`}
						</span>
					</div>
				</div>
			</div>

			{/* Promo Code Section */}
			{onPromoApply && ( // Only show if promo functionality is enabled
				<div className="pt-4 border-t border-gray-100">
					{promoAmount > 0 ? (
						// --- Applied Promo Display ---
						<div className="space-y-2">
							<p className="text-sm font-medium text-gray-700">
								Promo Code Applied:
							</p>
							<div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
								<span className="font-semibold text-green-700 flex items-center gap-1">
									{" "}
									<Tag size={14} /> {promoCode}
								</span>
								<button
									aria-label="Remove Promo Code"
									onClick={onPromoRemove} // Use passed handler
									disabled={isApplyingPromo} // Disable if currently applying another
									className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50"
								>
									<Trash2 size={16} />
								</button>
							</div>
						</div>
					) : (
						// --- Promo Input Form ---
						<div className="space-y-2">
							<label
								htmlFor="promo-code"
								className="block text-sm font-medium text-gray-700"
							>
								Have a Promo Code?
							</label>
							<div className="flex gap-2">
								<input
									id="promo-code"
									aria-label="Promo Code"
									type="text"
									value={promoCode}
									onChange={onPromoChange}
									placeholder="Enter code"
									disabled={isApplyingPromo}
									className={`form-input flex-grow ${
										promoError ? "border-red-500" : "border-gray-300"
									}`} // Use form-input style
								/>
								<button
									onClick={onPromoApply}
									disabled={isApplyingPromo || !promoCode.trim()}
									className="btn btn-secondary px-4 py-2 text-sm" // Use button styles
								>
									{isApplyingPromo ? <LoadingSpinner size="sm" /> : "Apply"}
								</button>
							</div>
							{promoError && (
								<p className="text-red-500 text-xs mt-1">{promoError}</p>
							)}
						</div>
					)}
				</div>
			)}

			{/* Checkout Button */}
			{onCheckout && (
				<div className="pt-5 border-t border-gray-100">
					<button
						onClick={onCheckout}
						disabled={isCheckingOut} // Disable while processing
						className="w-full btn btn-primary py-3 text-base" // Prominent button
					>
						{isCheckingOut ? (
							<LoadingSpinner size="sm" />
						) : (
							"Proceed to Checkout"
						)}
					</button>
				</div>
			)}
		</div>
	);
};

// Define base styles if not global (add to CSS/Tailwind config)
/*

*/
